import { STAGE_LABELS, normaliseStageChoice, type StageChoice } from '@nemo/core/model';

export interface StagePickerProps {
  choices: readonly StageChoice[];
  onChange: (choices: readonly StageChoice[]) => void;
  onCommit: () => void;
  onRevert: () => void;
}

/**
 * Two boxes per stage, Invests and Leads. Ticking Leads ticks Invests, since
 * leading a round is being in it, and clearing Invests clears Leads. Enter or
 * Done commits, Escape reverts, as every picklist here does.
 */
export function StagePicker(props: StagePickerProps): React.JSX.Element {
  function set(stage: StageChoice['stage'], change: Partial<StageChoice>): void {
    props.onChange(
      props.choices.map((choice) => {
        if (choice.stage !== stage) return choice;
        const next = { ...choice, ...change };
        // Unticking Invests is a no to both; ticking Leads is a yes to both.
        return change.participates === false
          ? { ...next, leads: false }
          : normaliseStageChoice(next);
      }),
    );
  }

  return (
    <div
      className="picklist stage-picker"
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          props.onCommit();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          props.onRevert();
        }
      }}
    >
      <table className="stage-picker-table">
        <thead>
          <tr>
            <th scope="col">Stage</th>
            <th scope="col">Invests</th>
            <th scope="col">Leads</th>
          </tr>
        </thead>
        <tbody>
          {props.choices.map((choice, index) => (
            <tr key={choice.stage}>
              <th scope="row">{STAGE_LABELS[choice.stage]}</th>
              <td>
                <input
                  type="checkbox"
                  autoFocus={index === 0}
                  aria-label={`Invests at ${STAGE_LABELS[choice.stage]}`}
                  checked={choice.participates}
                  onChange={(event) => set(choice.stage, { participates: event.target.checked })}
                />
              </td>
              <td>
                <input
                  type="checkbox"
                  aria-label={`Leads at ${STAGE_LABELS[choice.stage]}`}
                  checked={choice.leads}
                  onChange={(event) => set(choice.stage, { leads: event.target.checked })}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="button small" type="button" onClick={props.onCommit}>
        Done (Enter)
      </button>
    </div>
  );
}
